/**
 * ConversionModal — shown after sandbox analysis completes.
 * Encourages signup while allowing continued sandbox exploration.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, X, ArrowRight, Download, FlaskConical } from 'lucide-react';
import { useSandboxSession } from './hooks/useSandboxSession';

export default function ConversionModal({ onClose }) {
  const navigate = useNavigate();
  const { analysisResult, techStack, complianceNeeds, orgType } = useSandboxSession();
  const [dismissed, setDismissed] = useState(false);

  if (dismissed || !analysisResult) return null;

  const summary = analysisResult.summary;
  const gapCount = analysisResult.gaps?.totalGaps || 0;
  const pillarCount = summary?.pillarScores?.length || 0;

  // Build signup query params from non-sensitive sandbox metadata
  const signupParams = new URLSearchParams();
  if (techStack.length > 0) signupParams.set('stack', techStack.slice(0, 5).join(','));
  if (complianceNeeds.length > 0) signupParams.set('frameworks', complianceNeeds.join(','));
  if (orgType) signupParams.set('orgType', orgType);
  const signupUrl = `/signup?${signupParams.toString()}`;

  const handleSignup = () => {
    navigate(signupUrl);
  };

  const handleContinue = () => {
    setDismissed(true);
    onClose?.();
  };

  return (
    <div className="sb-modal-overlay" onClick={handleContinue}>
      <div className="sb-modal" onClick={e => e.stopPropagation()}>
        <button className="sb-modal-close" onClick={handleContinue} aria-label="Close">
          <X size={18} />
        </button>

        <div className="sb-modal-icon">
          <CheckCircle size={48} />
        </div>

        <h2 className="sb-modal-title">Your sandbox analysis is ready</h2>

        <p className="sb-modal-stats">
          You mapped <strong>{gapCount} gaps</strong> across <strong>{pillarCount} domains</strong> for
          a {orgType || 'custom'} organization.
        </p>

        <div className="sb-modal-benefits">
          <h3>Save this analysis and unlock:</h3>
          <ul>
            <li><CheckCircle size={14} /> Persistent results across sessions</li>
            <li><CheckCircle size={14} /> Remediation tracking and task assignment</li>
            <li><CheckCircle size={14} /> Team collaboration and evidence upload</li>
            <li><CheckCircle size={14} /> Audit-ready export (PDF, CSV)</li>
          </ul>
        </div>

        <div className="sb-modal-actions">
          <button className="sb-modal-btn sb-modal-btn--primary" onClick={handleSignup}>
            Create Free Account
            <ArrowRight size={16} />
          </button>
          <button className="sb-modal-btn sb-modal-btn--secondary" onClick={handleContinue}>
            <Download size={14} />
            Download Watermarked PDF
          </button>
        </div>

        <button className="sb-modal-continue" onClick={handleContinue}>
          <FlaskConical size={14} />
          Continue exploring in sandbox
        </button>
      </div>
    </div>
  );
}
